import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, EMPTY, Observable, catchError, finalize, tap } from 'rxjs';
import {
  IGenericServiceResponse,
  IServiceResponse,
} from '../interfaces/IServiceResponse';
import { IGetList } from '../interfaces/Get/IGetList';
import { environment } from 'src/environments/environment';
import { EpisodeOfCareService } from './episodeOfCare.service';
import { PatientService } from './patient.service';
import { IProblemsDetailed } from '../interfaces/Problems/IProblemsDetailed';
import { IAddProblem } from '../interfaces/Problems/IAddProblem';
import { IProblemDropdown } from '../interfaces/Problems/IProblemDropdown';
import { IRareDisease } from '../interfaces/Problems/IRareDiseaseDropdown';
import { IIllnessDisorderDropdown } from '../interfaces/Problems/IIllnessDisorderDropdown';
import { TranslateService } from '@ngx-translate/core';
import { SwalToastService } from './swal.service';

@Injectable({ providedIn: 'root' })
export class CurrentProblemsService {
  public currentProblemModalSub = new BehaviorSubject<boolean>(false);

  public currentProblemEditSub = new BehaviorSubject<
    IProblemsDetailed | undefined
  >(undefined);

  public currentProblemDataSub = new BehaviorSubject<
    IGetList<IProblemsDetailed>
  >({});

  public currentProblemLoadingSub = new BehaviorSubject<boolean>(false);
  public currentProblemLoadingSub$ =
    this.currentProblemLoadingSub.asObservable();

  public currentProblemNoKnownLoadingSub = new BehaviorSubject<boolean>(false);

  constructor(
    private readonly _http: HttpClient,
    private readonly eocService: EpisodeOfCareService,
    private readonly patientService: PatientService,
    private readonly translate: TranslateService,
    private readonly swalToastService: SwalToastService
  ) {}

  loadData(): Observable<IGenericServiceResponse<IGetList<IProblemsDetailed>>> {
    this.currentProblemLoadingSub.next(true);
    return this.getProblems(
      this.patientService.patientIDsignal(),
      this.eocService.currentVisitId()
    ).pipe(
      tap((response) => {
        this.currentProblemDataSub.next(response?.data!);
        this.currentProblemNoKnownLoadingSub.next(false);
      }),
      catchError((err) => {
        this.swalToastService.toastError('Unable to retrieve problems');
        console.error('Error fetching current problems:', err);
        return EMPTY;
      }),
      finalize(() => this.currentProblemLoadingSub.next(false)),
    );
  }

  getData(): void {
    this.loadData().subscribe();
  }

  getProblems(
    patientId: string,
    visitId?: string
  ): Observable<IGenericServiceResponse<IGetList<IProblemsDetailed>>> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    if (visitId) {
      return this._http.get<
        IGenericServiceResponse<IGetList<IProblemsDetailed>>
      >(
        `${environment.BACKEND_URL}Problems/CurrentProblems/${patientId}?visitId=${visitId}`,
        {
          headers: headers,
        }
      );
    } else {
      return this._http.get<
        IGenericServiceResponse<IGetList<IProblemsDetailed>>
      >(`${environment.BACKEND_URL}Problems/CurrentProblems/${patientId}`, {
        headers: headers,
      });
    }
  }

  getProblemDropdown(): Observable<IGenericServiceResponse<IProblemDropdown>> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    return this._http.get<IGenericServiceResponse<IProblemDropdown>>(
      `${environment.BACKEND_URL}Problems/DropdownData`,
      {
        headers: headers,
      }
    );
  }

  getRareDiseaseDropdown(): Observable<IGenericServiceResponse<IRareDisease>> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    return this._http.get<IGenericServiceResponse<IRareDisease>>(
      `${environment.BACKEND_URL}Problems/DropdownData/RareDisease`,
      {
        headers: headers,
      }
    );
  }

  getIllnessDisorderDropdown(): Observable<
    IGenericServiceResponse<IIllnessDisorderDropdown>
  > {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    return this._http.get<IGenericServiceResponse<IIllnessDisorderDropdown>>(
      `${environment.BACKEND_URL}Problems/DropdownData/IllnessAndDisorder`,
      {
        headers: headers,
      }
    );
  }

  addProblem(problemObj: IAddProblem) {
    problemObj.visitId = this.eocService.currentVisitId();
    problemObj.patientId = this.patientService.patientIDsignal();

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this._http.post(`${environment.BACKEND_URL}Problems`, problemObj, {
      headers: headers,
    });
  }

  addNoKnownProblem() {
    const object = {
      patientId: this.patientService.patientIDsignal(),
      visitId: this.eocService.currentVisitId(),
    };

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this._http.post(
      `${environment.BACKEND_URL}Problems/NoKnownProblems`,
      object,
      {
        headers: headers,
      }
    );
  }

  editProblem(problemObj: IAddProblem) {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this._http.put(
      `${environment.BACKEND_URL}Problems/${problemObj.id}`,
      problemObj,
      {
        headers: headers,
      }
    );
  }

  stopProblem(medicationId: string): Observable<IServiceResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    const visitId = this.eocService.currentVisitId();

    return this._http.put<IServiceResponse>(
      `${environment.BACKEND_URL}Problems/Stop/${medicationId}/${visitId}`,
      {
        headers: headers,
      }
    );
  }

  deleteProblems(problemId: string): Observable<IServiceResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this._http.delete<IServiceResponse>(
      `${environment.BACKEND_URL}Problems/${problemId}`,
      {
        headers: headers,
      }
    );
  }

  deleteNoKnownProblems(problemId: string): Observable<IServiceResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this._http.delete<IServiceResponse>(
      `${environment.BACKEND_URL}Problems/NoKnownProblems/${problemId}`,
      {
        headers: headers,
      }
    );
  }
}
